<template>
  <div class="card card-plain cancel-card" :class="{ 'cancel-card-dark': isDarkMode }">
    <div v-if="loading" class="card-body text-center py-4">Načítám…</div>
    <div v-else-if="step === 1" class="card-body pt-0">
      <div
        v-for="notice in notices"
        :key="notice.id"
        class="notice-card"
      >
        <div class="notice-head">
          <div class="notice-title-wrap">
            <div class="notice-card-title">{{ notice.title }}</div>
            <p v-if="notice.inlineText" class="notice-text" :class="{ 'notice-text-sm': notice.smallText }">{{ notice.inlineText }}</p>
          </div>
          <label class="notice-ack">
            <input v-model="ack[notice.id]" type="checkbox">
            Beru na vědomí
          </label>
        </div>
        <ul v-if="notice.games?.length">
          <li v-for="game in notice.games" :key="notice.id + '-' + game.GameID">
            {{ game.GameName }}
            <span v-if="game.phase" class="notice-phase">{{ game.phase }}</span>
          </li>
        </ul>
        <p v-if="notice.note" class="notice-note">{{ notice.note }}</p>
      </div>

      <div class="notice-actions">
        <span class="notice-action-slot">
          <argon-button color="secondary" variant="outline" size="md" @click="store.dispatch('closeModal')">
            <fa-icon icon="fa-solid fa-arrow-left" class="me-2" />
            Zpět
          </argon-button>
        </span>
        <span
          class="notice-action-slot"
          :class="{ 'is-blocked': !allAcked }"
          :title="allAcked ? undefined : 'Je nutné schválit podmínky zrušení účtu'"
        >
          <argon-button
            color="danger"
            size="md"
            :disabled="!allAcked"
            @click="step = 2"
          >
            Pokračovat
            <fa-icon icon="fa-solid fa-arrow-right" class="ms-2" />
          </argon-button>
        </span>
      </div>
    </div>

    <div v-else class="card-body pt-0">
      <p class="cancel-lead">Pro zrušení účtu zadej svoje heslo.</p>
      <argon-alert
        v-for="msg in errorMessages"
        :key="msg"
        class="text-sm text-center mt-2 mb-3"
        icon="fa-solid fa-triangle-exclamation"
        color="danger"
      >{{ msg }}</argon-alert>
      <form @submit.prevent="confirmCancel">
        <div class="mb-3">
          <input
            v-model="password"
            class="form-control form-control-lg"
            type="password"
            placeholder="Heslo"
            name="cancelPassword"
            autocomplete="current-password"
          >
        </div>
        <div class="mb-3">
          <textarea
            v-model="reason"
            class="form-control"
            rows="4"
            placeholder="Budu ráda, když mi sdělíš, proč odcházíš. Třeba mi to pomůže se zlepšit ... (nepovinné)"
            maxlength="2000"
          />
        </div>
        <div class="notice-actions">
          <span class="notice-action-slot">
            <argon-button
              type="button"
              color="secondary"
              variant="outline"
              size="md"
              @click="step = 1; errorMessages = []"
            >
              <fa-icon icon="fa-solid fa-arrow-left" class="me-2" />
              Zpět
            </argon-button>
          </span>
          <span class="notice-action-slot">
            <argon-button
              type="submit"
              color="danger"
              size="md"
              :disabled="isButtonLoading"
            >
              <span v-if="isButtonLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
              Nevratně zrušit účet
            </argon-button>
          </span>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonAlert from "@/components/ArgonAlert.vue";
import authService from "@/services/authService";
import { formatDashIfWhole } from "@/utils/NumberFormat";

const store = useStore();
const swal = inject("$swal");
const isDarkMode = computed(() => !!store.state.isDarkMode);
const isButtonLoading = computed(() => store.getters["loadingModule/isButtonLoading"]);
const userID = computed(() => store.getters.getUser?.UserID);
const sessionHash = computed(() => store.getters.isLoggedIn);

const loading = ref(true);
const step = ref(1);
const preview = ref({ OpenGame: [], WaitingGame: [], RunningGame: [], Credit: 0, CreditBonus: 0 });
const password = ref("");
const reason = ref("");
const errorMessages = ref([]);

const asGameList = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object" && value.GameID != null) return [value];
  return [];
};

const openGames = computed(() => asGameList(preview.value.OpenGame));
const waitingGames = computed(() => asGameList(preview.value.WaitingGame));
const runningGames = computed(() => asGameList(preview.value.RunningGame));
const paidCredit = computed(() => Number(preview.value.Credit) || 0);
const bonusCredit = computed(() => Number(preview.value.CreditBonus) || 0);
const hasCredit = computed(() => paidCredit.value > 0.005);
const hasBonus = computed(() => bonusCredit.value > 0.005);
const creditLabel = computed(() => `${formatDashIfWhole(paidCredit.value)} Kč`);
const bonusLabel = computed(() => `${formatDashIfWhole(bonusCredit.value)} Kč`);
const ack = reactive({});

const notices = computed(() => {
  const list = [];
  if (runningGames.value.length) {
    list.push({
      id: "running",
      title: "Běžící hry, kterých se účastníš",
      games: runningGames.value,
      note: "Tyto hry už nebude možné dohrát a nezískáš ani případné výhry.",
    });
  }
  const openIds = {};
  const pendingGames = [];
  openGames.value.forEach((game) => {
    openIds[String(game.GameID)] = true;
    pendingGames.push({ ...game, phase: "v registraci" });
  });
  waitingGames.value.forEach((game) => {
    if (openIds[String(game.GameID)]) return;
    pendingGames.push({ ...game, phase: "čeká na start" });
  });
  if (pendingGames.length) {
    list.push({
      id: "waiting",
      title: "Čekající hry a hry v registraci",
      games: pendingGames,
      note: "Tyto hry už nebude možné dohrát.",
    });
  }
  if (hasCredit.value) {
    list.push({
      id: "credit",
      title: "Kredit",
      inlineText: `${creditLabel.value} už nebude možné využít.`,
    });
  }
  if (hasBonus.value) {
    list.push({
      id: "bonus",
      title: "Bonusový kredit",
      inlineText: `${bonusLabel.value} už nebude možné využít.`,
    });
  }
  list.push({
    id: "login",
    title: "Přihlášení",
    inlineText: "Po zrušení účtu již nebude možné se přihlásit.",
    smallText: true,
  });
  return list;
});

const allAcked = computed(() => notices.value.length > 0 && notices.value.every((notice) => !!ack[notice.id]));

onMounted(async () => {
  loading.value = true;
  try {
    const response = await authService.GetUserObsolete(userID.value, sessionHash.value);
    if (response?.Result) {
      preview.value = response.Data || preview.value;
    } else {
      store.dispatch("closeModal");
      swal.fire({
        position: "center",
        icon: "error",
        html: `${(response?.ErrorMessage || ["Náhled se nepodařilo načíst"]).map((m) => `<h5>${m}</h5>`).join("")}`,
        showConfirmButton: false,
        timer: 3000,
      });
    }
  } finally {
    loading.value = false;
  }
});

const confirmCancel = async () => {
  errorMessages.value = [];
  if (!password.value) {
    errorMessages.value = ["Zadej svoje heslo"];
    return;
  }
  store.dispatch("loadingModule/setButtonLoading", true);
  try {
    const response = await authService.UserObsolete(
      userID.value,
      sessionHash.value,
      password.value,
      reason.value
    );
    if (response?.Result) {
      await store.dispatch("logout", { skipApi: true });
      swal.fire({
        position: "center",
        icon: "success",
        html: `${(response.InfoMessage || ["Tvůj profil byl zrušen"]).map((m) => `<h5>${m}</h5>`).join("")}`,
        showConfirmButton: false,
        timer: 3000,
      });
    } else {
      errorMessages.value = response?.ErrorMessage || ["Profil se nepodařilo zrušit"];
    }
  } finally {
    store.dispatch("loadingModule/setButtonLoading", false);
  }
};
</script>

<style scoped>
.cancel-card :deep(.card-body) {
  padding-bottom: 0.5rem;
}

.cancel-lead {
  font-size: 0.85rem;
  color: #475569;
  margin-bottom: 0.65rem;
}

.cancel-card-dark .cancel-lead {
  color: #CBD5E1;
}

.notice-card {
  text-align: left;
  margin-bottom: 0.7rem;
  padding: 0.55rem 0.7rem 0.5rem;
  border-radius: 0.55rem;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.cancel-card-dark .notice-card {
  background: #1E293B;
  border-color: #334155;
}

.notice-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
}

.notice-title-wrap {
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  min-width: 0;
  flex: 1 1 auto;
}

.notice-card-title {
  font-size: 0.78rem;
  font-weight: 800;
  color: #FF8A4C;
  line-height: 1.2;
  margin: 0;
  flex-shrink: 0;
}

.notice-title-wrap .notice-text {
  margin: 0;
}

.notice-text-sm {
  font-size: 0.68rem !important;
}

.notice-card ul {
  margin: 0.35rem 0 0;
  padding-left: 1.05rem;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.55;
  color: #1E293B;
}

.notice-card ul li + li {
  margin-top: 0.2rem;
}

.cancel-card-dark .notice-card ul {
  color: #FFFFFF;
}

.notice-phase {
  font-weight: 500;
  color: #94A3B8;
  margin-left: 0.35rem;
  font-size: 0.7rem;
}

.notice-text,
.notice-note {
  font-size: 0.75rem;
  line-height: 1.4;
  color: #64748B;
  margin: 0.35rem 0 0;
}

.cancel-card-dark .notice-text,
.cancel-card-dark .notice-note {
  color: #B8C4D4;
}

.notice-ack {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748B;
  cursor: pointer;
  white-space: nowrap;
}

.cancel-card-dark .notice-ack {
  color: #94A3B8;
}

.notice-ack input {
  width: 0.85rem;
  height: 0.85rem;
  margin: 0;
  accent-color: #FF8A4C;
  cursor: pointer;
}

.notice-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-top: 0.95rem;
}

.notice-action-slot {
  display: block;
  min-width: 0;
}

.notice-action-slot :deep(.btn) {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: normal;
  line-height: 1.25;
}

.notice-action-slot.is-blocked {
  cursor: not-allowed;
}

.notice-action-slot.is-blocked :deep(.btn) {
  pointer-events: none;
}
</style>
